import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useDataProvider } from 'react-admin';

type RewardsPendingCountContextValue = {
  pendingCount: number | null;
  pendingCountLoading: boolean;
  pendingCountError: string | null;
  refreshPendingCount: () => Promise<void>;
};

const RewardsPendingCountContext = createContext<RewardsPendingCountContextValue>({
  pendingCount: null,
  pendingCountLoading: false,
  pendingCountError: null,
  refreshPendingCount: async () => {},
});

export const RewardsPendingCountProvider = ({ children }: { children: React.ReactNode }) => {
  const dataProvider = useDataProvider();
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [pendingCountLoading, setPendingCountLoading] = useState(false);
  const [pendingCountError, setPendingCountError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  // Prevent stale responses from overwriting newer pending counts.
  const requestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refreshPendingCount = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    try {
      setPendingCountLoading(true);
      setPendingCountError(null);
      const response = await dataProvider.getList('rewardsVerificationList', {
        pagination: { page: 1, perPage: 1 },
        filter: { status: 'PENDING' },
      });

      if (isMountedRef.current && requestId === requestIdRef.current) {
        setPendingCount(response?.total ?? 0);
      }
    } catch (error) {
      if (isMountedRef.current && requestId === requestIdRef.current) {
        setPendingCountError('Failed to load pending count');
        setPendingCount(null);
      }
    } finally {
      if (isMountedRef.current && requestId === requestIdRef.current) {
        setPendingCountLoading(false);
      }
    }
  }, [dataProvider]);

  useEffect(() => {
    refreshPendingCount();
  }, [refreshPendingCount]);

  return (
    <RewardsPendingCountContext.Provider
      value={{ pendingCount, pendingCountLoading, pendingCountError, refreshPendingCount }}>
      {children}
    </RewardsPendingCountContext.Provider>
  );
};

export const useRewardsPendingCount = () => useContext(RewardsPendingCountContext);
